import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../utils/reduxHelper.js";

export const homePageReduxAPI = createApi({
  reducerPath: "homePageReduxAPI",
  baseQuery: baseQueryWithArgs,
  tagTypes: ["Masterclass"],

  endpoints: (builder) => ({
    fetchBrandDetailsById: builder.query({
      query: ({ brand_id }) =>
        getQueryStringWithCreator(
          `c/fetchBrandDetailsById?brand_id=${brand_id}`
        ),
    }),
    fetchCreatorTestimonials: builder.query({
      query: () => getQueryStringWithCreator(`c/fetchCreatorTestimonials`),
    }),
    fetchCreatorHghlights: builder.query({
      query: () => getQueryStringWithCreator(`c/getStoryDetails`),
    }),
    updateCreatorStoryHighlights: builder.mutation({
      query: (body) => ({
        url: `/c/updateStoryDetails`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateCreatorEventTrackingLogs: builder.mutation({
      query: (body) => ({
        url: `/c/updateCreatorEventTrackingLogs`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    getCreatorActiveGtms: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorActiveGtms`),
    }),
    getCreatorWarnings: builder.query({
      query: () => getQueryStringWithCreator(`c/getCreatorBulletins`),
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    getWishlinkAccountDetails: builder.query({
      query: () => getQueryStringWithCreator(`c/getWishlinkAccountDetails`),
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    updateWishlinkAccountProfile: builder.mutation({
      query: (body) => ({
        url: `/c/createWishlinkAccountProfile`,
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateShowHideBlacklistBanner: builder.mutation({
      query: (body) => ({
        url: `/c/updateShowHideBlacklistBanner`,
        method: "PATCH",
        body: postQueryBodyWithCreator(body),
      }),
    }),
    updateReactivateCreatorProfile: builder.mutation({
      query: (body) => ({
        url: `/c/reactivateCreatorProfile`,
        method: "POST",
        body,
      }),
    }),
    getWishlinkWrappedDetails: builder.query({
      query: () => getQueryStringWithCreator(`/yearroundup/wrapped24/`),
      transformResponse: (response) => {
        return response;
      },
    }),
    updateTopProductsCollection: builder.mutation({
      query: () => ({
        url: `/yearroundup/wrapped24Collection/`,
        method: "POST",
        body: {},
      }),
    }),
    getMasterclassData: builder.query({
      query: () => getQueryStringWithCreator(`c/getMasterclassData`),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["Masterclass"],
    }),
    getMasterclassSectionsData: builder.query({
      query: ({ masterclass_id }) =>
        getQueryStringWithCreator(
          `c/getMasterclassSectionsData?masterclass_id=${masterclass_id}`
        ),
      providesTags: ["Masterclass"],
    }),
    getMasterclassSectionVideos: builder.query({
      query: ({ section_id }) =>
        getQueryStringWithCreator(
          `c/getMasterclassSectionVideos?section_id=${section_id}`
        ),
      providesTags: ["Masterclass"],
    }),
    updateMasterclassVideoCurrentlyPlaying: builder.mutation({
      query: (body) => ({
        url: `/c/updateMasterclassVideoCurrentlyPlaying`,
        method: "PATCH",
        body: postQueryBodyWithCreator(body),
      }),
      // Home card reads progress and the resume video off getMasterclassData, so both
      // progress writes have to drop that cache.
      invalidatesTags: ["Masterclass"],
    }),
    updateMasterclassVideoCompletePercent: builder.mutation({
      query: (body) => ({
        url: `/c/updateMasterclassVideoCompletePercent`,
        method: "PATCH",
        body: postQueryBodyWithCreator(body),
      }),
      invalidatesTags: ["Masterclass"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFetchBrandDetailsByIdQuery,
  useLazyFetchBrandDetailsByIdQuery,
  useLazyFetchCreatorTestimonialsQuery,
  useLazyFetchCreatorHghlightsQuery,
  useUpdateCreatorStoryHighlightsMutation,
  useUpdateCreatorEventTrackingLogsMutation,
  useLazyGetCreatorActiveGtmsQuery,
  useLazyGetCreatorWarningsQuery,
  useGetCreatorWarningsQuery,
  useGetWishlinkAccountDetailsQuery,
  useUpdateWishlinkAccountProfileMutation,
  useUpdateShowHideBlacklistBannerMutation,
  useUpdateReactivateCreatorProfileMutation,
  useGetWishlinkWrappedDetailsQuery,
  useUpdateTopProductsCollectionMutation,
  useGetMasterclassDataQuery,
  useGetMasterclassSectionsDataQuery,
  useUpdateMasterclassVideoCurrentlyPlayingMutation,
  useUpdateMasterclassVideoCompletePercentMutation,
} = homePageReduxAPI;
